// context/BrandingContext.tsx
// React Context für Subdomain-Branding
// Macht Branding-Daten in der gesamten App verfügbar

'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// ============================================================
// Typen
// ============================================================

export interface BrandingInfo {
  id: string
  bestatter_id: string
  subdomain: string
  brand_name: string | null
  brand_logo_url: string | null
  brand_favicon_url: string | null
  brand_primary_color: string
  brand_secondary_color: string
  brand_background_color: string
  brand_text_color: string
  welcome_title: string | null
  welcome_text: string | null
  footer_text: string | null
  show_stille_sterne_branding: boolean
  is_active: boolean
  bestatter_name?: string
  bestatter_company?: string
  bestatter_logo?: string
}

interface BrandingContextType {
  branding: BrandingInfo | null
  isSubdomain: boolean
  subdomain: string | null
  isLoading: boolean
  error: string | null
}

// ============================================================
// Context
// ============================================================

const BrandingContext = createContext<BrandingContextType>({
  branding: null,
  isSubdomain: false,
  subdomain: null,
  isLoading: true,
  error: null,
})

// ============================================================
// Hook
// ============================================================

export function useBranding(): BrandingContextType {
  return useContext(BrandingContext)
}

// ============================================================
// Root-Domain Helper (Client-Side)
// ============================================================

function getClientRootDomain(): string {
  // Aus der Umgebungsvariable
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_ROOT_DOMAIN) {
    return process.env.NEXT_PUBLIC_ROOT_DOMAIN
  }
  // Aus SITE_URL parsen
  const siteUrl = typeof process !== 'undefined' 
    ? process.env?.NEXT_PUBLIC_SITE_URL || '' 
    : ''
  try {
    const url = new URL(siteUrl)
    const parts = url.hostname.split('.')
    if (parts.length > 2 && parts[0] === 'www') {
      return parts.slice(1).join('.')
    }
    return url.hostname
  } catch {
    return 'stillesterne.de'
  }
}

function extractSubdomainClient(): { subdomain: string | null; isSubdomain: boolean } {
  if (typeof window === 'undefined') {
    return { subdomain: null, isSubdomain: false }
  }
  
  const hostname = window.location.hostname
  const rootDomain = getClientRootDomain()
  
  // Localhost / Dev
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    // Dev-Modus: Query-Parameter ?subdomain=mueller zum Testen
    const params = new URLSearchParams(window.location.search)
    const devSubdomain = params.get('subdomain')
    if (devSubdomain) {
      return { subdomain: devSubdomain, isSubdomain: true }
    }
    return { subdomain: null, isSubdomain: false }
  }
  
  // Vercel Preview
  if (hostname.includes('.vercel.app')) {
    return { subdomain: null, isSubdomain: false }
  }
  
  // Prüfen ob Hostname zur Root-Domain gehört
  if (!hostname.endsWith(rootDomain)) {
    return { subdomain: null, isSubdomain: false }
  }
  
  // Subdomain extrahieren
  const prefix = hostname.replace(`.${rootDomain}`, '').replace(rootDomain, '')
  
  if (!prefix || prefix === 'www') {
    return { subdomain: null, isSubdomain: false }
  }
  
  return { subdomain: prefix, isSubdomain: true }
}

// ============================================================
// Provider
// ============================================================

interface BrandingProviderProps {
  children: ReactNode
  // Optional: Server-Side vorgeladenes Branding (für schnellere Anzeige)
  initialBranding?: BrandingInfo | null
  initialSubdomain?: string | null
}

export function BrandingProvider({ 
  children, 
  initialBranding = null,
  initialSubdomain = null 
}: BrandingProviderProps) {
  const [branding, setBranding] = useState<BrandingInfo | null>(initialBranding)
  const [subdomain, setSubdomain] = useState<string | null>(initialSubdomain)
  const [isSubdomain, setIsSubdomain] = useState(!!initialSubdomain)
  const [isLoading, setIsLoading] = useState(!initialBranding && !initialSubdomain)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Subdomain Client-Side erkennen
    const { subdomain: clientSubdomain, isSubdomain: clientIsSubdomain } = extractSubdomainClient()
    
    // Wenn Server-Side schon Branding geliefert hat, nicht nochmal laden
    if (initialBranding && initialSubdomain) {
      setSubdomain(initialSubdomain)
      setIsSubdomain(true)
      setBranding(initialBranding)
      setIsLoading(false)
      return
    }
    
    if (!clientIsSubdomain || !clientSubdomain) {
      setSubdomain(null)
      setIsSubdomain(false)
      setBranding(null)
      setIsLoading(false)
      return
    }
    
    setSubdomain(clientSubdomain)
    setIsSubdomain(true)
    
    // Branding von API laden
    const loadBranding = async () => {
      try {
        setIsLoading(true)
        const res = await fetch(`/api/branding?subdomain=${encodeURIComponent(clientSubdomain)}`)
        
        if (!res.ok) {
          throw new Error('Branding nicht gefunden')
        }
        
        const data = await res.json()
        
        if (data.branding) {
          setBranding(data.branding)
          
          // Favicon dynamisch setzen
          if (data.branding.brand_favicon_url) {
            updateFavicon(data.branding.brand_favicon_url)
          }
          
          // Document Title anpassen
          if (data.branding.brand_name) {
            const currentTitle = document.title
            if (!currentTitle.includes(data.branding.brand_name)) {
              document.title = `${data.branding.brand_name} | ${currentTitle}`
            }
          }
          
          // CSS Custom Properties setzen
          applyBrandingStyles(data.branding)
        }
      } catch (err: any) {
        console.error('[BrandingProvider] Error:', err)
        setError(err.message || 'Branding konnte nicht geladen werden')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadBranding()
  }, [initialBranding, initialSubdomain])

  return (
    <BrandingContext.Provider value={{ branding, isSubdomain, subdomain, isLoading, error }}>
      {children}
    </BrandingContext.Provider>
  )
}

// ============================================================
// Hilfsfunktionen
// ============================================================

function updateFavicon(url: string) {
  try {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = url
  } catch {
    // Fehler ignorieren
  }
}

function applyBrandingStyles(branding: BrandingInfo) {
  try {
    const root = document.documentElement
    root.style.setProperty('--brand-primary', branding.brand_primary_color)
    root.style.setProperty('--brand-secondary', branding.brand_secondary_color)
    root.style.setProperty('--brand-background', branding.brand_background_color)
    root.style.setProperty('--brand-text', branding.brand_text_color)
  } catch {
    // Fehler ignorieren
  }
}

// ============================================================
// Branding-Aware Komponenten
// ============================================================

// Logo-Komponente: Zeigt Bestatter-Logo oder Stille-Sterne-Logo
export function BrandedLogo({ 
  fallbackSrc, 
  className = '',
  width = 150,
  height = 50 
}: { 
  fallbackSrc: string
  className?: string
  width?: number
  height?: number
}) {
  const { branding, isSubdomain } = useBranding()
  
  const logoUrl = isSubdomain && branding?.brand_logo_url 
    ? branding.brand_logo_url 
    : fallbackSrc
  
  const altText = isSubdomain && branding?.brand_name 
    ? branding.brand_name 
    : 'Stille Sterne'
  
  return (
    <img 
      src={logoUrl} 
      alt={altText}
      className={className}
      width={width}
      height={height}
      style={{ objectFit: 'contain' }}
    />
  )
}

// Footer-Branding: Zeigt optionalen "Powered by Stille Sterne" Text
export function BrandedFooter({ className = '' }: { className?: string }) {
  const { branding, isSubdomain } = useBranding()
  
  if (!isSubdomain) return null
  
  return (
    <div className={className} style={{ textAlign: 'center', padding: '16px 0' }}>
      {branding?.footer_text && (
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
          {branding.footer_text}
        </p>
      )}
      {branding?.show_stille_sterne_branding && (
        <p style={{ fontSize: '12px', color: '#9ca3af' }}>
          Powered by{' '}
          <a 
            href="https://stillesterne.de" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#6b7280', textDecoration: 'underline' }}
          >
            Stille Sterne
          </a>
        </p>
      )}
    </div>
  )
}
